import {createTheme,ThemeProvider,alpha} from '@mui/material/styles';
import {Box,GlobalStyles} from '@mui/material';
import poolImage from '../assets/background.jpg';

// Consumer-only look (athletes), inspired by teamrazzo.com: deep-water navy bar,
// steel-blue actions, pale mint page, Days One display headings over Montserrat
// body, pill buttons and soft 20px frosted cards over a blurred pool backdrop.
// Lane4's own amber (lane4.fi) is kept for the brand moments (login / home CTAs).
// Staff keep the core theme (core/components/Theme.js, driven by
// AppRole.main_color) - this is applied as a nested ThemeProvider around the
// consumer header, bottom nav and pages only, so app-level providers above them
// (snackbar, confirm, loading) stay on the core theme. Fonts are loaded in
// public/index.html.
export const CONSUMER_COLORS={
    navy:'#001623',
    deepTeal:'#003b54',
    steelBlue:'#0073a4',
    cyan:'#88f9f9',
    pink:'#ee739a',
    mint:'#f8fffb',
    mintDark:'#f0f7f3',
    lane4Amber:'#ffbc00',
};

export const LANE4_WEBSITE='https://www.lane4.fi/';
export const POOL_IMAGE=poolImage;

export const isConsumer=(userData)=>userData?.role_name==='consumer';

const DISPLAY_FONT='"Days One","Montserrat",sans-serif';
const BODY_FONT='"Montserrat","Roboto","Helvetica","Arial",sans-serif';

const displayHeading=(uppercase)=>({
    fontFamily:DISPLAY_FONT,
    fontWeight:400,//Days One ships a single weight
    letterSpacing:'0.02em',
    ...(uppercase?{textTransform:'uppercase'}:{}),
});

// Frosted glass so the pool backdrop reads through cards without hurting contrast.
const GLASS={
    backgroundColor:alpha('#ffffff',0.82),
    backdropFilter:'blur(12px) saturate(140%)',
    WebkitBackdropFilter:'blur(12px) saturate(140%)',
};

const buildConsumerTheme=({bar=false}={})=>createTheme({
    palette:{
        // The header AppBar paints itself with primary.main, so the bar variant
        // swaps in the navy while pages keep steel blue for actions.
        primary:{main:bar?CONSUMER_COLORS.navy:CONSUMER_COLORS.steelBlue,contrastText:'#fff'},
        secondary:{main:CONSUMER_COLORS.pink,contrastText:'#fff'},
        lane4:{main:CONSUMER_COLORS.lane4Amber,contrastText:CONSUMER_COLORS.navy},
        background:{default:CONSUMER_COLORS.mint,paper:'#fff'},
        text:{primary:CONSUMER_COLORS.navy,secondary:alpha(CONSUMER_COLORS.deepTeal,0.72)},
        divider:alpha(CONSUMER_COLORS.deepTeal,0.12),
    },
    list_link_color:CONSUMER_COLORS.steelBlue,
    shape:{borderRadius:7},//sx borderRadius:3 -> 21px cards, matching the site
    typography:{
        fontFamily:BODY_FONT,
        h1:displayHeading(true),
        h2:displayHeading(true),
        h3:displayHeading(true),
        h4:displayHeading(true),
        h5:displayHeading(true),
        h6:displayHeading(false),//Also used by DialogTitle and card headings - no shouting there
        subtitle1:{fontWeight:600},
        subtitle2:{fontWeight:600},
        button:{textTransform:'none',fontWeight:600},
    },
    components:{
        MuiButton:{
            defaultProps:{disableElevation:true},
            styleOverrides:{root:{borderRadius:100}},
        },
        MuiFab:{
            styleOverrides:{root:{boxShadow:`0 8px 20px ${alpha(CONSUMER_COLORS.steelBlue,0.35)}`}},
        },
        MuiChip:{
            styleOverrides:{
                root:{fontWeight:600},
                outlined:{backgroundColor:alpha('#ffffff',0.7)},
            },
        },
        MuiCard:{
            styleOverrides:{root:{borderRadius:20,...GLASS}},
        },
        MuiPaper:{
            styleOverrides:{outlined:{borderColor:alpha(CONSUMER_COLORS.deepTeal,0.12),...GLASS}},
        },
        MuiBottomNavigation:{
            styleOverrides:{root:{...GLASS,backgroundColor:alpha('#ffffff',0.9)}},
        },
        MuiBottomNavigationAction:{
            styleOverrides:{
                root:{'&.Mui-selected':{color:CONSUMER_COLORS.steelBlue}},
                label:{fontWeight:600,'&.Mui-selected':{fontSize:'0.75rem'}},
            },
        },
    },
});

const PAGE_THEME=buildConsumerTheme();
const BAR_THEME=buildConsumerTheme({bar:true});

// Fixed page backdrop for consumer screens, painted over core Background.js
// (same pool picture, 10% opacity on white). The photo is low-res portrait, so it
// is blurred and scaled - that hides the softness and turns it into a calm water
// texture: mint at the top where page titles sit, the pool showing through
// towards the bottom, plus two soft cyan/steel-blue glows for depth.
export function ConsumerBackdrop(){
    return (
        <Box aria-hidden sx={{position:'fixed',inset:0,zIndex:-1,overflow:'hidden',bgcolor:CONSUMER_COLORS.mint,pointerEvents:'none'}}>
            <Box sx={{
                position:'absolute',
                inset:'-24px',
                backgroundImage:`url(${poolImage})`,
                backgroundSize:'cover',
                backgroundPosition:'center 70%',
                filter:'blur(6px) saturate(120%)',
                opacity:0.5,
            }}/>
            <Box sx={{
                position:'absolute',
                inset:0,
                background:[
                    `radial-gradient(60% 45% at 12% 0%, ${alpha(CONSUMER_COLORS.cyan,0.35)} 0%, transparent 70%)`,
                    `radial-gradient(50% 40% at 100% 15%, ${alpha(CONSUMER_COLORS.steelBlue,0.18)} 0%, transparent 70%)`,
                    `linear-gradient(180deg, ${CONSUMER_COLORS.mint} 0%, ${alpha(CONSUMER_COLORS.mint,0.92)} 30%, ${alpha(CONSUMER_COLORS.mint,0.7)} 70%, ${alpha(CONSUMER_COLORS.mintDark,0.55)} 100%)`,
                ].join(','),
            }}/>
        </Box>
    );
}

// force: apply even when the user is not (yet) a consumer - the logged-out
// landing page and its header are the public face of the app.
// backdrop: also render the fixed pool backdrop (only one instance needed).
function ConsumerThemeProvider({userData,bar=false,force=false,backdrop=false,children}){
    if(!force&&!isConsumer(userData)){
        return children;
    }
    return (
        <ThemeProvider theme={bar?BAR_THEME:PAGE_THEME}>
            <GlobalStyles styles={{body:{backgroundColor:CONSUMER_COLORS.mint}}}/>
            {backdrop&&<ConsumerBackdrop/>}
            {children}
        </ThemeProvider>
    );
}

export default ConsumerThemeProvider;
